import { API_ENDPOINTS } from '../config/api';
import { authFetch } from './authFetch';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CorrelationSummary {
  total_p1p2: number;
  total_p1: number;
  total_p2: number;
  with_p3p4_precursors: number;
  with_alert_signals: number;
  with_any_signal: number;
  high_confidence_count: number;
  no_precursors_count: number;
  precursor_rate: number;
  monitoring_gap_rate: number;
  from_date: string;
  to_date: string;
}

export type CorrelationConfidence = 'High' | 'Medium' | 'Low' | null;
export type GapType =
  | 'monitoring_triggered'
  | 'alerts_not_acted'
  | 'monitoring_no_escalation'
  | 'monitoring_gap'
  | 'no_ci_mapping';

export interface CorrelationIncident {
  number: string;
  priority: string;
  short_description: string;
  sys_created_on_value: string;
  resolved_at: string | null;
  cmdb_ci_display_value: string | null;
  cmdb_ci_u_application_id: string | null;
  assignment_group_display_value: string | null;
  contact_type_display_value: string | null;
  state_display_value: string | null;
  outage_duration: number | null;
  close_code: string | null;
  portfolio_name: string | null;
  platform_name: string | null;
  precursor_count: number;
  /** Sum of actual alert_count values across 12h-before + incident duration */
  alert_signal_count: number;
  /** Alerts that fired in the 12 hours BEFORE the incident opened */
  pre_incident_alert_count: number;
  /** Alerts that fired DURING the incident (open → resolved) */
  during_incident_alert_count: number;
  peak_alert_count: number;
  best_correlation_confidence: CorrelationConfidence;
  gap_type: GapType;
  has_precursors: boolean;
}

export interface PrecursorIncident {
  number: string;
  priority: string;
  short_description: string;
  sys_created_on_value: string;
  resolved_at: string | null;
  cmdb_ci_display_value: string | null;
  cmdb_ci_u_application_id: string | null;
  assignment_group_display_value: string | null;
  state_display_value: string | null;
  close_code: string | null;
  correlation_confidence: 'High' | 'Medium' | 'Low';
  correlation_reasons: string[];
  matched_keywords?: string[];
  minutes_before_p1p2: number | null;
}

export interface AlertSignal {
  snr_value: number | null;
  alert_count: number | null;
  quick_resolved_alerts: number | null;
  scope_name: string | null;
  link: string | null;
  calculated_at: string;
  /** 'pre_incident' = fired in 12h before open; 'during_incident' = fired while incident was open */
  window_type: 'pre_incident' | 'during_incident';
}

export interface IncidentDetail {
  incident: CorrelationIncident & {
    work_notes: string;
    close_notes: string;
    impact: string | null;
    urgency: string | null;
    category_display_value: string | null;
    u_owner_display_value: string | null;
    caused_by_display_value: string | null;
  };
  precursors: PrecursorIncident[];
  alert_signals: AlertSignal[];
  correlation_summary: {
    precursor_count: number;
    alert_signal_count: number;
    pre_incident_alert_count: number;
    during_incident_alert_count: number;
    best_confidence: CorrelationConfidence;
    gap_type: GapType;
    has_precursors: boolean;
    keywords_used: string[];
  };
}

export interface IncidentsListResponse {
  success: boolean;
  data: CorrelationIncident[];
  total_count: number;
  limit: number;
  offset: number;
  has_more: boolean;
  filters: {
    from_date: string;
    to_date: string;
    confidence: string;
    gap_type: string;
    portfolio: string;
    platform: string;
    user_reported_only: boolean;
  };
}

export interface FilterOptions {
  portfolios: string[];
  platforms: string[];
}

export interface WeeklyTrendItem {
  week_start: string;
  total_p1p2: number;
  monitoring_triggered: number;
  not_captured: number;
  with_early_warning: number;
  early_warning_rate: number;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export async function fetchCorrelationSummary(
  fromDate: string,
  toDate: string,
  opts: { portfolio?: string; platform?: string; userReportedOnly?: boolean } = {},
): Promise<CorrelationSummary> {
  const qs = new URLSearchParams({
    from_date: fromDate,
    to_date: toDate,
    ...(opts.portfolio          ? { portfolio: opts.portfolio }                                : {}),
    ...(opts.platform           ? { platform:  opts.platform  }                                : {}),
    ...(opts.userReportedOnly   ? { user_reported_only: 'true' }                               : {}),
  });
  const res = await authFetch(`${API_ENDPOINTS.PRE_INCIDENT_CORRELATION.SUMMARY}?${qs}`);
  if (!res.ok) throw new Error(`Summary request failed: ${res.status}`);
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Failed to fetch summary');
  return json.data as CorrelationSummary;
}

export async function fetchCorrelationIncidents(params: {
  fromDate: string;
  toDate: string;
  limit?: number;
  offset?: number;
  confidence?: string;
  gapType?: string;
  portfolio?: string;
  platform?: string;
  userReportedOnly?: boolean;
}): Promise<IncidentsListResponse> {
  const {
    fromDate, toDate,
    limit = 50, offset = 0,
    confidence = '', gapType = '',
    portfolio = '', platform = '',
    userReportedOnly = false,
  } = params;
  const qs = new URLSearchParams({
    from_date: fromDate,
    to_date: toDate,
    limit: String(limit),
    offset: String(offset),
    ...(confidence        ? { confidence }                         : {}),
    ...(gapType           ? { gap_type: gapType }                  : {}),
    ...(portfolio         ? { portfolio }                          : {}),
    ...(platform          ? { platform }                           : {}),
    ...(userReportedOnly  ? { user_reported_only: 'true' }         : {}),
  });
  const res = await authFetch(`${API_ENDPOINTS.PRE_INCIDENT_CORRELATION.INCIDENTS}?${qs}`);
  if (!res.ok) throw new Error(`Incidents request failed: ${res.status}`);
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Failed to fetch incidents');
  return json as IncidentsListResponse;
}

export async function fetchFilterOptions(): Promise<FilterOptions> {
  const res = await authFetch(API_ENDPOINTS.PRE_INCIDENT_CORRELATION.FILTER_OPTIONS);
  if (!res.ok) throw new Error(`Filter options request failed: ${res.status}`);
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Failed to fetch filter options');
  return { portfolios: json.portfolios, platforms: json.platforms };
}

export async function fetchWeeklyTrend(
  fromDate: string,
  toDate: string,
  opts: { portfolio?: string; platform?: string } = {},
): Promise<WeeklyTrendItem[]> {
  const qs = new URLSearchParams({
    from_date: fromDate,
    to_date: toDate,
    ...(opts.portfolio ? { portfolio: opts.portfolio } : {}),
    ...(opts.platform  ? { platform:  opts.platform  } : {}),
  });
  const res = await authFetch(`${API_ENDPOINTS.PRE_INCIDENT_CORRELATION.WEEKLY_TREND}?${qs}`);
  if (!res.ok) throw new Error(`Weekly trend request failed: ${res.status}`);
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Failed to fetch weekly trend');
  return json.data as WeeklyTrendItem[];
}

export async function fetchIncidentCorrelationDetail(
  incidentNumber: string
): Promise<IncidentDetail> {
  const res = await authFetch(API_ENDPOINTS.PRE_INCIDENT_CORRELATION.INCIDENT_DETAIL(incidentNumber));
  if (!res.ok) throw new Error(`Detail request failed: ${res.status}`);
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Failed to fetch detail');
  return json.data as IncidentDetail;
}

export interface LiveAlertsScope {
  scope_name: string;
  raw_alert_count: number;
  non_prod_filtered: number;
  total_alerts: number;
  pre_incident_alerts: number;
  during_incident_alerts: number;
  quick_resolved_alerts: number;
}

export interface LiveAlertsResult {
  success: boolean;
  incident_number: string;
  mac_id: string;
  app_name: string;
  data: {
    window_start: string;
    window_end: string;
    scopes_queried: string[];
    raw_alert_count: number;
    non_prod_filtered_count: number;
    total_alert_count: number;
    pre_incident_alert_count: number;
    during_incident_alert_count: number;
    quick_resolved_count: number;
    snr_value: number | null;
    alerts_by_scope: LiveAlertsScope[];
  };
}

export async function fetchIncidentLiveAlerts(
  incidentNumber: string,
  saveToDb = false,
): Promise<LiveAlertsResult> {
  const url = `${API_ENDPOINTS.PRE_INCIDENT_CORRELATION.INCIDENT_LIVE_ALERTS(incidentNumber)}${
    saveToDb ? '?save=true' : ''
  }`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`Live alerts request failed: ${res.status}`);
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Failed to fetch live alerts');
  return json as LiveAlertsResult;
}
